import React from 'react';
import { Select, type SelectOption } from '@chatsdet/ui';
import { useLinearTeams } from '../../../hooks/useLinearIntegration';
import { useFilingOptions } from './filingOptions';
import type { TicketingProviderSummary } from '../../../hooks/useTicketingProviders';

type TicketingProviderOptions = {
  optionFields: React.ReactNode;
  providerOptions?: Record<string, unknown>;
  shouldConfirmBeforeCreate: boolean;
};

export function useTicketingProviderOptions(
  provider: TicketingProviderSummary,
  isConfiguring: boolean,
  issueId: string,
  canCreate: boolean,
  onLinked: () => void
): TicketingProviderOptions {
  const providerId = provider.id;
  const [linearTeamId, setLinearTeamId] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setLinearTeamId(undefined);
  }, [providerId]);

  const linearTeamsQuery = useLinearTeams(providerId === 'linear' && isConfiguring);
  const filing = useFilingOptions({
    providerId,
    enabled: canCreate && provider.capabilities.filingSettings,
    isConfiguring,
    issueId,
    onLinked,
  });

  const linearTeamOptions = React.useMemo<SelectOption[]>(
    () =>
      (linearTeamsQuery.data?.data ?? []).map((team) => ({
        id: team.id,
        label: `${team.name} (${team.key})`,
      })),
    [linearTeamsQuery.data]
  );

  if (provider.capabilities.filingSettings) return filing;

  if (providerId !== 'linear') {
    return {
      optionFields: null,
      providerOptions: undefined,
      shouldConfirmBeforeCreate: false,
    };
  }

  return {
    optionFields:
      linearTeamOptions.length > 1 ? (
        <Select
          aria-label="Linear team"
          selectedKey={linearTeamId ?? null}
          items={linearTeamOptions}
          placeholder="Default team"
          onSelectionChange={(key) => setLinearTeamId(key ? String(key) : undefined)}
        />
      ) : null,
    providerOptions: linearTeamId ? { teamId: linearTeamId } : undefined,
    shouldConfirmBeforeCreate: true,
  };
}
